import React from "react";
import ReactDOM from "react-dom/client";
import App from "./App";
import MobileApp from "./mobile/MobileApp";
import ReviewProposalPage from "./ReviewProposalPage";
import ErrorBoundary from "./ErrorBoundary";
import ResetPasswordPage from "./ResetPasswordPage";
import AcceptInvitePage from "./AcceptInvitePage";
import * as serviceWorkerRegistration from "./serviceWorkerRegistration";
import { markSwUpdateReady } from "./swUpdate";
import { purgeDegenerateNotesKeys } from "./shared/proposalSend";
import { dlog } from "./debugLog";
import { APP_VERSION } from "./version";
// Loaded last on purpose: this sheet fixes light-mode field visibility
// (visible box at rest, obvious ring on focus) and has to settle ties
// with styles.css / theme.css / mobile.css, which are pulled in by the
// component imports above. Light mode only — dark is untouched.
import "./inputVisibility.css";

// Scrub the poisoned blank-name Notes key ("du_proposal_notes::") left behind
// by the name-keyed builds, before anything renders. Done here rather than in a
// shell so desktop, mobile and the public /review route all clean up — the bad
// value is per-device, so every entry point has to do it. Named legacy keys are
// left alone; the migration fallback still reads them.
try {
  const scrubbed = purgeDegenerateNotesKeys();
  if (scrubbed.length) {
    dlog("[notes] purged degenerate notes key(s):", scrubbed);
  }
} catch {
  /* never block startup on a cache cleanup */
}

/**
 * Detect whether this device should render the mobile shell.
 *
 * Locked at first paint: the same device must stay on the same shell for
 * the whole session, even when the viewport changes (phone rotation,
 * software-keyboard show/hide). Previously this was a live matchMedia
 * subscription, which on rotation flipped a phone above the 480px width
 * breakpoint and remounted the entire tree — discarding auth + estimate
 * state before the debounced cloud-save fired.
 *
 * Phones (touch + no hover) almost always have one viewport dimension
 * ≤ 480px, regardless of orientation, so we test min(width, height).
 * A narrow desktop window also gets the mobile layout, matching the
 * old breakpoint behavior; that decision is still frozen at mount, so
 * resizing the desktop window mid-session won't tear down the tree.
 */
function detectMobile(): boolean {
  if (typeof window === "undefined") return false;
  const coarsePointer =
    typeof window.matchMedia === "function" &&
    window.matchMedia("(pointer: coarse) and (hover: none)").matches;
  const smallestSide = Math.min(window.innerWidth, window.innerHeight);
  if (coarsePointer && smallestSide <= 480) return true;
  return window.matchMedia("(max-width: 480px)").matches;
}

/**
 * Public customer routes. These render BEFORE the mobile-vs-desktop
 * branch and BEFORE any auth gate, so a customer opening a /review/{id}
 * link in a browser with no session — typical case — always sees the
 * proposal. The mobile shell's auth gate (MobileApp.tsx) would otherwise
 * intercept the URL and show AuthPage to the customer.
 *
 * App.tsx:504 has the same check for its own internal routing; keeping
 * this here makes the public path independent of which shell loads.
 */
function isPublicCustomerPath(pathname: string): boolean {
  return pathname.startsWith("/review/");
}

/**
 * Password-recovery landing route. Same public-path treatment as
 * /review/ — a user clicking the reset link in their email arrives
 * here logged-out (or in a Supabase recovery session) and must reach
 * the "set new password" form without being intercepted by either
 * shell's auth gate.
 */
function isResetPasswordPath(pathname: string): boolean {
  return pathname === "/reset-password" || pathname.startsWith("/reset-password/");
}

/**
 * Invite landing route (the emailed invite link lands here). Public for the
 * same reason as /reset-password: the invitee has no company yet, so either
 * shell's auth gate would intercept them.
 */
function isAcceptInvitePath(pathname: string): boolean {
  return pathname === "/accept-invite" || pathname.startsWith("/accept-invite/");
}

function Root() {
  // Hook must run in stable order — call useState first, branch after.
  const [isMobile] = React.useState<boolean>(detectMobile);

  // Hand the launch splash (public/index.html) over to the real UI. The splash
  // is what the window shows while this bundle downloads and mounts; dismissing
  // it here — inside an effect, after a frame — means it fades onto a drawn
  // screen rather than onto a half-built one. Its own failsafe removes it if we
  // never get this far, so a throw above this point can't strand the user.
  React.useEffect(() => {
    const w = window as unknown as {
      __duSplashHide?: () => void;
      __duSplashVersion?: (v: string) => void;
    };
    try {
      w.__duSplashVersion?.(APP_VERSION);
      requestAnimationFrame(() => w.__duSplashHide?.());
    } catch {
      /* the splash failsafe covers us */
    }
  }, []);

  const path =
    typeof window !== "undefined" && window.location
      ? window.location.pathname
      : "";

  if (isPublicCustomerPath(path)) {
    return <ReviewProposalPage />;
  }

  if (isResetPasswordPath(path)) {
    return <ResetPasswordPage />;
  }

  if (isAcceptInvitePath(path)) {
    return <AcceptInvitePage />;
  }

  return isMobile ? <MobileApp /> : <App />;
}

const root = ReactDOM.createRoot(
  document.getElementById("root") as HTMLElement
);
// Wrap the whole tree: an uncaught render throw used to unmount everything and
// leave a white screen with no message and no way back — the worst possible
// failure for a tool used mid-appointment. Mounted OUTSIDE Root so it also
// catches a throw from the shell-detection branch itself.
root.render(
  <ErrorBoundary>
    <Root />
  </ErrorBoundary>
);

if (process.env.NODE_ENV === "production") {
  // markSwUpdateReady flips the store the sidebar footer subscribes to, which
  // is what surfaces the "Update available — Refresh" pill. Registration runs
  // outside the React tree, hence the store rather than a prop.
  serviceWorkerRegistration.register(markSwUpdateReady);
} else {
  serviceWorkerRegistration.unregister();
}
